import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Snackbar,
  Alert,
  IconButton,
  Drawer,
  Tooltip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import Sidebar from "../components/Sidebar";
import {
  getVacationRequests,
  approveVacationRequest,
  rejectVacationRequest,
} from "../api/services";

import VacationStats from "../components/VacationStats";
import VacationRequestsTable from "../components/VacationRequestsTable";
import VacationActionDialog from "../components/VacationActionDialog";

// ---- Tokens used only by the main file ----
const ui = {
  primary: "#009db9",
  text: "#1f2933",
};

const PageHeader = ({ title }) => (
  <Box sx={{ mb: { xs: 3, md: 4 }, mt: { xs: 4, sm: 5 } }}>
    <Typography
      variant="h4"
      sx={{
        fontWeight: "bold",
        color: ui.text,
        letterSpacing: 0.5,
        fontSize: { xs: "1.5rem", sm: "1.85rem", md: "2.125rem" },
        position: "relative",
        "&::after": {
          content: '""',
          display: "block",
          backgroundColor: ui.primary,
          height: "4px",
          width: { xs: "80px", sm: "100px" },
          position: "absolute",
          left: 0,
          bottom: "-8px",
        },
      }}
    >
      {title}
    </Typography>
  </Box>
);

const MenuButton = ({ onClick }) => (
  <Box
    sx={{
      display: { xs: "block", md: "none" },
      position: "fixed",
      top: { xs: 70, sm: 72 },
      left: { xs: 12, sm: 16 },
      zIndex: 1100,
      mb: 2,
    }}
  >
    <Tooltip title="Menu" arrow>
      <IconButton
        onClick={onClick}
        sx={{
          background: "linear-gradient(135deg, #009db9 0%, #007a8a 100%)",
          color: "#fff",
          borderRadius: "12px",
          p: 1.2,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          "&:hover": {
            background: "linear-gradient(135deg, #007a8a 0%, #005b66 100%)",
            boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
          },
          transition: "all 0.3s ease",
        }}
        aria-label="toggle sidebar"
      >
        <MenuIcon sx={{ fontSize: "1.6rem" }} />
      </IconButton>
    </Tooltip>
  </Box>
);

const VacationManagement = () => {
  const [vacationData, setVacationData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filter, setFilter] = useState("All");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openModal, setOpenModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionType, setActionType] = useState("");
  const [comment, setComment] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // ---- Fetch vacation requests ----
  const fetchVacationData = async () => {
    try {
      const responses = await Promise.all([
        getVacationRequests("Pending", 0, 1000),
        getVacationRequests("Approved", 0, 1000),
        getVacationRequests("Rejected", 0, 1000),
      ]);

      const allRequests = responses
        .flat()
        .filter((request) => request && typeof request === "object")
        .map((request) => ({
          ...request,
          comment: request.comment || "",
          createdAt: request.createdAt || new Date().toISOString(),
        }));

      if (allRequests.length === 0) {
        const allResponse = await getVacationRequests("All", 0, 1000);
        allRequests.push(
          ...(Array.isArray(allResponse) ? allResponse : []).map((request) => ({
            ...request,
            comment: request.comment || "",
            createdAt: request.createdAt || new Date().toISOString(),
          }))
        );
      }

      setVacationData(allRequests);
      setTotalRecords(allRequests.length);
    } catch (error) {
      console.error("Error in fetchVacationData:", error);
      setSnackbarMessage("Error fetching vacation requests. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setVacationData([]);
      setTotalRecords(0);
    } finally {
      setDataLoading(false);
    }
  };

  // ---- Filter + paginate ----
  useEffect(() => {
    let dataToDisplay = vacationData;
    if (filter !== "All") {
      const statusToFilter =
        filter === "Rejected" ? "DENIED" : filter.toUpperCase();
      dataToDisplay = vacationData.filter(
        (request) => request.status === statusToFilter
      );
    }

    const startIndex = page * rowsPerPage;
    const paginatedData = dataToDisplay.slice(
      startIndex,
      startIndex + rowsPerPage
    );

    setFilteredData(paginatedData);
    setTotalRecords(dataToDisplay.length);
  }, [filter, vacationData, page, rowsPerPage]);

  useEffect(() => {
    fetchVacationData();
  }, []);

  // ---- Pagination ----
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ---- Modal ----
  const handleOpenModal = (request, type) => {
    setSelectedRequest(request);
    setActionType(type);
    setComment("");
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedRequest(null);
    setActionType("");
    setComment("");
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // ---- Submit action ----
  const handleSubmitAction = async () => {
    if (!selectedRequest || !comment.trim()) {
      setSnackbarMessage("A comment is required to process the request.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

    setActionLoading(true);
    try {
      const response =
        actionType === "Accept"
          ? await approveVacationRequest(selectedRequest.id, comment)
          : await rejectVacationRequest(selectedRequest.id, comment);

      const updatedRequest = {
        ...selectedRequest,
        status: actionType === "Accept" ? "APPROVED" : "DENIED",
        comment: response?.comment || comment,
        createdAt: response?.createdAt || new Date().toISOString(),
      };
      const updatedVacationData = vacationData.map((request) =>
        request.id === selectedRequest.id ? updatedRequest : request
      );
      setVacationData(updatedVacationData);

      setSnackbarMessage(
        `Vacation request ${actionType.toLowerCase()}ed successfully!`
      );
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage(
        `Error ${actionType.toLowerCase()}ing vacation request`
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setActionLoading(false);
    }

    handleCloseModal();
  };

  // ---- Drawer ----
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // ---- Derived ----
  const countByStatus = (status) =>
    vacationData.filter((r) => r.status === status).length;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Box
        sx={{
          maxWidth: "1200px",
          mx: "auto",
          width: "100%",
          flexGrow: 1,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Box sx={{ display: { xs: "none", md: "block" } }}>
          <Sidebar />
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            p: { xs: 2, md: 4 },
            pt: { xs: 12, sm: 14, md: 10 },
            backgroundColor: "#fff",
            minHeight: "100vh",
            ml: { md: "20px" },
            pr: { xs: 1, md: 5 },
            position: "relative",
          }}
        >
          <MenuButton onClick={handleDrawerToggle} />

          <PageHeader title="Vacation Management" />

          <VacationStats
            total={vacationData.length.toLocaleString()}
            pending={countByStatus("PENDING").toLocaleString()}
            approved={countByStatus("APPROVED").toLocaleString()}
            denied={countByStatus("DENIED").toLocaleString()}
          />

          <VacationRequestsTable
            dataLoading={dataLoading}
            filteredData={filteredData}
            totalRecords={totalRecords}
            filter={filter}
            setFilter={setFilter}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleOpenModal={handleOpenModal}
          />
        </Box>
      </Box>

      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          zIndex: 1200,
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: 250,
            top: { xs: 0, sm: 0 },
            height: "100%",
            borderRight: "none",
          },
        }}
      >
        <Sidebar onItemClick={handleDrawerToggle} />
      </Drawer>

      <VacationActionDialog
        openModal={openModal}
        actionType={actionType}
        actionLoading={actionLoading}
        selectedRequest={selectedRequest}
        comment={comment}
        setComment={setComment}
        handleCloseModal={handleCloseModal}
        handleSubmitAction={handleSubmitAction}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%", borderRadius: "10px" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VacationManagement;